import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "./CheckoutForm";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { fetchWithTimeout } from "../../utils/fetchWithTimeout";

// How long CreatePaymentIntent is allowed to sit idle before we tell the user
// it looks slow, and the hard cutoff before we give up waiting entirely.
const INTENT_SLOW_THRESHOLD_MS = 6000;
const INTENT_TIMEOUT_MS = 20000;

const STRIPE_LOCALE_MAP = { cn: "zh", id: "id", en: "en" };

const StripePayment = ({
  id,
  amount,
  currency,
  isOpen,
  onClose,
  onPaymentSuccess,
}) => {
  const { i18n } = useTranslation();
  const stripeLocale = STRIPE_LOCALE_MAP[i18n.language] || "auto";

  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(false);
  const [slowConnection, setSlowConnection] = useState(false);
  const [error, setError] = useState(null);

  // Stripe requires the amount to be an integer in the smallest currency unit.
  // For SGD/USD/EUR etc., that's cents (e.g., 1.00 SGD => 100).
  // For zero-decimal currencies (e.g., JPY, KRW), use the whole number.
  // Note: We no longer scale the amount on the client; the server should handle
  // converting to the correct units as needed.
  const toMinorUnits = (amt) => {
    if (amt == null) return amt;
    const n = typeof amt === "string" ? parseFloat(amt) : Number(amt);
    if (!isFinite(n)) return amt;
    // Convert to cents for USD-like currencies.
    // e.g. 6.99 -> 699
    return Math.round(n * 100);
  };

  useEffect(() => {
    // Load Stripe only on the client side
    const promise = loadStripe(
      "pk_live_51RjwCNH4SEOW1T3d8NbnBAfQFvuPQjXVIlqDEuAiKXkAnjk1opYg86mCJdU6aikNOisXhwyADFh9VRq6Ue9r88gi00HTySzJLr"
    );
    setStripePromise(promise);
  }, []);

  // useEffect(() => {
  //   // Load Stripe only on the client side
  //   const promise = loadStripe(
  //     "pk_test_51RjwCNH4SEOW1T3dMgaqKZwCHyUcSceyurYaPwcqjWrOEpLNK56QsBsBOxCHvBZZINHy0H6bnN2w2PN0svXmyTFT00EBj8nalv"
  //   );
  //   setStripePromise(promise);
  // }, []);

  useEffect(() => {
    const createPaymentIntent = async () => {
      // Force currency to USD for Stripe payments
      const currencyCode = "USD"; // always use USD
      // Convert amount (e.g. 6.99) to minor units (cents) expected by Stripe/backend
      const minorAmount = toMinorUnits(amount);
      let coinbody = {
        id: "coins",
        currency: currencyCode,
        // amount is sent as provided (no client-side scaling)
        // amount should be an integer in minor units (cents for USD). Example: 699
        amount: minorAmount,
        // include the same value for clarity; server should decide how to interpret
        amountMajor:
          typeof amount === "string" ? parseFloat(amount) : Number(amount),
      };
      let settled = false;
      const slowTimer = setTimeout(() => {
        if (!settled) setSlowConnection(true);
      }, INTENT_SLOW_THRESHOLD_MS);

      try {
        setLoading(true);
        setSlowConnection(false);
        setError(null);

        // Log the exact amounts we're about to send so it's visible in browser console
        console.log("Creating payment intent (no scaling on client, forced USD):", { amount, minorAmount, currency: currencyCode, coinbody });

        const response = await fetchWithTimeout(
          "https://xianhefairy.com/LotusFairy_API/Payment/CreatePaymentIntent",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(coinbody),
          },
          INTENT_TIMEOUT_MS
        );
        if (!response.ok) {
          // attempt to read the body (text) to provide more diagnostic info
          let txt = "";
          try {
            txt = await response.text();
          } catch (e) {
            txt = `<unable to read response body: ${e.message}>`;
          }
          console.error("CreatePaymentIntent failed", response.status, txt);
          throw new Error("Failed to create payment intent: " + txt);
        }

        const data = await response.json();
        setClientSecret(data.clientSecret);
        console.log("CreatePaymentIntent response:", data);
        console.log("this is body ", coinbody);
      } catch (error) {
        setError(
          error?.name === "TimeoutError"
            ? "Your connection seems slow and we couldn't start the payment in time. Please check your internet connection and try again."
            : "Error creating payment intent"
        );
        console.log("this is body ", coinbody);
      } finally {
        settled = true;
        clearTimeout(slowTimer);
        setLoading(false);
        setSlowConnection(false);
      }
    };

    if (isOpen) {
      createPaymentIntent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, amount, currency]);

  const appearance = { theme: "stripe" };

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 9999999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "white",
          borderRadius: 12,
          padding: 16,
          paddingTop: 40,
          boxShadow: "0px 4px 16px rgba(0,0,0,0.3)",
          display: "flex",
          flexDirection: "column",
          width: "min(98vw, 640px)",
          maxWidth: "98vw",
          maxHeight: "calc(100vh - 32px)",
          overflow: "hidden",
          zIndex: 100000,
          position: "relative",
        }}
      >
        {/* Close button - Absolutely positioned to the top right to strictly avoid the Stripe Link invisible iframe */}
        <button
          type="button"
          style={{
            position: "absolute",
            top: 12,
            right: 28,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontSize: "32px",
            color: "black",
            padding: 0,
            margin: 0,
            lineHeight: 1,
            zIndex: 2147483647,
            width: 44,
            height: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
          onMouseDown={onClose}
          onClick={onClose}
          aria-label="Close"
        >
          <IoIosCloseCircleOutline style={{ display: "block" }} />
        </button>

        {/* Scrollable content area */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            minHeight: 0,
            width: "100%",
          }}
        >
          {loading ? (
            <p>
              {slowConnection
                ? "This is taking longer than usual — your connection may be slow. Please keep this window open..."
                : "Loading..."}
            </p>
          ) : error ? (
            <p>{error}</p>
          ) : clientSecret && stripePromise ? (
            <Elements
              stripe={stripePromise}
              options={{ clientSecret, appearance, locale: stripeLocale }}
            >
              <CheckoutForm
                onClose={onClose}
                onPaymentSuccess={onPaymentSuccess}
              />
            </Elements>
          ) : (
            <p>Unable to initialize payment. Please try again later.</p>
          )}
        </div>

      </div>
    </div>,
    document.body
  );
};

export default StripePayment;
